import { useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  fetchHierarchyByLevel,
  fetchHierarchyTree,
  findHierarchyPath,
  type HierarchyLevel,
  type HierarchyTreeNode,
} from '@/api/productHierarchy'
import { queryKeys } from '@/lib/queryKeys'
import { usePlanWizardStore } from '@/stores/usePlanWizardStore'

// Hierarchy data is relatively static, cache for 10 minutes
const STALE_TIME = 10 * 60 * 1000

export function useDepartments() {
  return useQuery({
    queryKey: queryKeys.hierarchy.departments(),
    queryFn: () => fetchHierarchyByLevel('DEPARTMENT'),
    staleTime: STALE_TIME,
  })
}

export function useClasses(departmentId: string | null) {
  return useQuery({
    queryKey: queryKeys.hierarchy.classes(departmentId!),
    queryFn: () => fetchHierarchyByLevel('CLASS', departmentId!),
    enabled: !!departmentId,
    staleTime: STALE_TIME,
  })
}

export function useSubclasses(classId: string | null) {
  return useQuery({
    queryKey: queryKeys.hierarchy.subclasses(classId!),
    queryFn: () => fetchHierarchyByLevel('SUBCLASS', classId!),
    enabled: !!classId,
    staleTime: STALE_TIME,
  })
}

export function useHierarchyLevel(
  level: HierarchyLevel | null,
  parentId?: string | null
) {
  return useQuery({
    queryKey: queryKeys.hierarchy.byLevelFiltered(level!, parentId ?? null),
    queryFn: () => fetchHierarchyByLevel(level!, parentId ?? undefined),
    enabled: !!level,
    staleTime: STALE_TIME,
  })
}

function flattenTree(
  nodes: HierarchyTreeNode[],
  map: Record<string, string>
): void {
  for (const node of nodes) {
    map[node.id] = `${node.code} - ${node.name}`
    if (node.children) flattenTree(node.children, map)
  }
}

/**
 * Returns a map of hierarchy node id → "code - name" for all levels.
 * Uses the cached hierarchy tree so it's essentially free after first load.
 */
export function useHierarchyLabelMap(): Record<string, string> {
  const { data: tree } = useQuery({
    queryKey: queryKeys.hierarchy.tree(),
    queryFn: fetchHierarchyTree,
    staleTime: STALE_TIME,
  })

  return useMemo(() => {
    const map: Record<string, string> = {}
    if (tree) flattenTree(tree, map)
    return map
  }, [tree])
}

/**
 * Hook to resolve and set hierarchy from a source plan's hierarchyId
 * Auto-populates department, class, and subclass in the wizard store
 */
export function useHierarchyFromPlan(hierarchyId: string | undefined) {
  const { setDepartment, setClass, setSubclass } = usePlanWizardStore()

  useEffect(() => {
    if (!hierarchyId) return

    findHierarchyPath(hierarchyId).then(path => {
      if (path.departmentId) setDepartment(path.departmentId)
      if (path.classId) setClass(path.classId)
      if (path.subclassId) setSubclass(path.subclassId)
    })
  }, [hierarchyId, setDepartment, setClass, setSubclass])
}
